import { LayoutHeader } from "@/Header";
import { ValidateSelectList } from "@/SelectList";
import { Dialog } from "@/Dialog";
// import { IconDownload } from '@/Icon/Download';
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import plusCircle from "$/plus-circle.svg";
import trashSrc from "$/trash.svg";
import { Button } from "@/Button";
import {
  useBusinessPhotoForm,
  useBusinessPhotoUploadPolicy,
  uploadBusinessPhoto,
} from "~/services/business/photo";
import type { BusinessPhotoUploadPolicy } from "~/types/common";
import { useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useCallback, useRef } from "react";
import { validateMediaFiles } from "../../../utils/fileValidation";
import { useConfirmTouch } from "../../../hooks/common/useConfirmTouch";
import { AnimatePresence, motion } from "framer-motion";
import { useLoadingStore } from "&/loading";
// [성능 개선] VideoThumbnail 비활성화 - Canvas 기반 썸네일 생성이 메모리 과다 사용 (~32MB/동영상)
// 서버측 썸네일 생성 구현 전까지 플레이스홀더로 대체
// import { VideoThumbnail } from "@/common/VideoThumbnail";
import {
  UploadProgressModal,
  type UploadProgressItem,
} from "@/common/UploadProgressModal";

export const Route = createFileRoute("/business/layout/gallery-add")({
  component: GalleryAdd,
});

// iOS 네이티브 앱 체크
const isIOSNative = () => /MT_I/.test(navigator.userAgent);

// Android 네이티브 앱 체크
const isAndroidNative = () => /MT_A/.test(navigator.userAgent);

// iOS 네이티브 미디어 아이템 타입
interface NativeMediaItem {
  id: string;
  type: "image" | "video";
  thumbnail: string;
  fileName: string;
  fileSize: number;
  duration?: number;
  width: number;
  height: number;
}

const FILE_TOO_LARGE_ERROR = "ERROR:FILE_TOO_LARGE";

// 파일 크기 초과 에러 체크
const isFileTooLargeError = (item: NativeMediaItem): boolean => {
  return item.thumbnail === FILE_TOO_LARGE_ERROR;
};

const getAllowedMediaType = (allowedTypes: Array<"image" | "video">) => {
  const allowImage = allowedTypes.includes("image");
  const allowVideo = allowedTypes.includes("video");

  if (allowImage && allowVideo) return "all";
  if (allowImage) return "image";
  if (allowVideo) return "video";
  return null;
};

const getFileInputAccept = (allowedTypes: Array<"image" | "video">) => {
  const accepts: string[] = [];
  if (allowedTypes.includes("image")) accepts.push("image/*");
  if (allowedTypes.includes("video")) accepts.push("video/*");
  return accepts.join(",");
};

function GalleryAdd() {
  const navigate = useNavigate();
  const { toggleLoading } = useLoadingStore();
  const queryClient = useQueryClient();

  // API 호출
  const { data: photoFormData, isLoading, error } = useBusinessPhotoForm();
  const {
    data: uploadPolicyData,
    isLoading: isPolicyLoading,
    error: policyError,
  } = useBusinessPhotoUploadPolicy();
  const uploadPolicy = uploadPolicyData?.data;
  const uploadPolicyRef = useRef<BusinessPhotoUploadPolicy | null>(null);

  useEffect(() => {
    if (uploadPolicy) {
      uploadPolicyRef.current = uploadPolicy;
    }
  }, [uploadPolicy]);

  // 선택된 반려견 상태
  const [selectedPet, setSelectedPet] = useState<string>("");

  // 업로드된 미디어들 상태 (이미지/동영상)
  const [uploadedMedia, setUploadedMedia] = useState<
    Array<{
      ImgID: number | string; // iOS는 string ID 사용
      ImgURL: string;
      mediaType: "image" | "video";
      fileName?: string;
      isNative?: boolean; // iOS 네이티브 미디어 여부
    }>
  >([]);

  const videoCount = uploadedMedia.filter((m) => m.mediaType === "video").length;
  const imageCount = uploadedMedia.filter((m) => m.mediaType === "image").length;
  const canAddMedia = uploadPolicy
    ? uploadedMedia.length < uploadPolicy.maxMediaCount
    : false;
  const [muteVideos, setMuteVideos] = useState(false);

  // 실제 파일들 상태 (API 전송용 - Android/Web만 사용)
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  // 업로드 진행률 상태
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState(1);  // 현재 업로드 중인 파일 번호 (1-based)
  const [uploadProgressItems, setUploadProgressItems] = useState<UploadProgressItem[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const nativeUploadInProgressRef = useRef(false);

  // Dialog 상태
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showValidationErrorDialog, setShowValidationErrorDialog] =
    useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // useConfirmTouch 훅 사용
  const { bindClick, idle } = useConfirmTouch();

  const syncNativeUploadPolicy = useCallback(() => {
    const currentPolicy = uploadPolicyRef.current;
    if (!currentPolicy) return;

    const policyJson = JSON.stringify(currentPolicy);

    if (isIOSNative() && window.webkit?.messageHandlers?.mung) {
      window.webkit.messageHandlers.mung.postMessage([
        "setMediaUploadPolicy",
        policyJson,
      ]);
    }

    if (isAndroidNative() && window.mung?.setMediaUploadPolicy) {
      window.mung.setMediaUploadPolicy(policyJson);
    }
  }, []);

  useEffect(() => {
    if (!uploadPolicyData?.data) return;
    syncNativeUploadPolicy();
  }, [uploadPolicyData, syncNativeUploadPolicy]);

  // 네이티브 콜백 등록 (iOS와 Android 공통 사용)
  useEffect(() => {
    if (!isIOSNative() && !isAndroidNative()) return;

    // 로딩 타임아웃 ref
    let loadingTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const clearLoadingTimeout = () => {
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
        loadingTimeoutId = null;
      }
    };

    const setLoadingTimeout = () => {
      clearLoadingTimeout();
      // 15초 타임아웃 - 동영상 처리가 오래 걸릴 수 있으므로 여유있게
      loadingTimeoutId = setTimeout(() => {
        console.log("⚠️ 네이티브 미디어 처리 타임아웃 - 로딩 강제 해제");
        toggleLoading(false);
      }, 15000);
    };

    // picker 타임아웃 해제 헬퍼
    const clearPickerTimeout = () => {
      const pickerTimeoutId = (window as unknown as { _pickerTimeoutId?: ReturnType<typeof setTimeout> })._pickerTimeoutId;
      if (pickerTimeoutId) {
        clearTimeout(pickerTimeoutId);
        (window as unknown as { _pickerTimeoutId?: ReturnType<typeof setTimeout> })._pickerTimeoutId = undefined;
      }
    };

    // 미디어 선택 결과 콜백 (점진적 업데이트 지원)
    window.onMediaSelectResult = (jsonString: string) => {
      // 결과가 왔으므로 picker 타임아웃 해제
      clearPickerTimeout();

      try {
        const result = JSON.parse(jsonString);

        // 점진적 업데이트 처리
        // 에러 처리 (success: false) - 타임아웃 등
        if (result.success === false) {
          clearLoadingTimeout();
          toggleLoading(false);
          if (result.error && result.error !== "선택 취소") {
            console.error("네이티브 미디어 선택 에러:", result.error);
            setErrorMessage(result.error);
            setShowErrorDialog(true);
          }
          return;
        }

        if (result.status === "processing") {
          // 처리 시작 - 로딩 유지 + 타임아웃 설정
          console.log("네이티브 미디어 처리 시작:", result.total, "개");
          setLoadingTimeout();
          return;
        }

        if (result.status === "item" && result.item) {
          // 개별 아이템 도착 - 즉시 UI에 추가
          const item = result.item as NativeMediaItem;

          // [파일 크기 검증] 네이티브에서 전달한 용량 초과 파일 에러 처리
          if (isFileTooLargeError(item)) {
            const fileSizeMB = (item.fileSize / 1024 / 1024).toFixed(1);
            const maxFileSizeMb = uploadPolicyRef.current?.maxFileSizeMb;
            const sizeLimitText = maxFileSizeMb
              ? `${maxFileSizeMb}MB`
              : "허용 용량";
            setErrorMessage(
              `"${item.fileName}"의 크기가 ${fileSizeMB}MB로 ${sizeLimitText} 제한을 초과했습니다.`
            );
            setShowErrorDialog(true);
            console.log(`네이티브: 파일 크기 초과 - ${item.fileName} (${fileSizeMB}MB)`);
            return;
          }

          setUploadedMedia((prev) => {
            const maxMediaCount = uploadPolicyRef.current?.maxMediaCount;
            if (!maxMediaCount) {
              setErrorMessage("업로드 정책을 불러오지 못했습니다.");
              setShowErrorDialog(true);
              return prev;
            }

            // 최대 개수 체크
            if (prev.length >= maxMediaCount) {
              setErrorMessage(`사진/동영상은 최대 ${maxMediaCount}개까지 업로드 가능합니다.`);
              setShowErrorDialog(true);
              return prev;
            }

            // 중복 체크
            if (prev.some((m) => m.ImgID === item.id)) {
              return prev;
            }

            return [
              ...prev,
              {
                ImgID: item.id,
                ImgURL: item.thumbnail,
                mediaType: item.type,
                fileName: item.fileName,
                isNative: true,
              },
            ];
          });

          console.log("네이티브 미디어 아이템 추가:", item.id);
          return;
        }

        if (result.status === "complete") {
          // 모든 처리 완료 - 타임아웃 해제 + 로딩 해제
          clearLoadingTimeout();
          toggleLoading(false);
          console.log("네이티브 미디어 선택 완료:", result.count, "개");
          return;
        }

        // 기존 방식 호환 (items 배열로 한번에 오는 경우)
        if (result.success && result.items) {
          clearLoadingTimeout();
          toggleLoading(false);
          const items = result.items as NativeMediaItem[];

          // [파일 크기 검증] 네이티브에서 전달한 용량 초과 파일 필터링
          const oversizedFiles = items.filter(isFileTooLargeError);
          const validItems = items.filter((item) => !isFileTooLargeError(item));

          // 초과 파일이 있으면 알림
          if (oversizedFiles.length > 0) {
            const fileNames = oversizedFiles.map((f) => f.fileName).join(", ");
            const maxFileSizeMb = uploadPolicyRef.current?.maxFileSizeMb;
            const sizeLimitText = maxFileSizeMb
              ? `${maxFileSizeMb}MB`
              : "허용 용량";
            setErrorMessage(
              `다음 파일이 ${sizeLimitText} 제한을 초과하여 제외되었습니다: ${fileNames}`
            );
            setShowErrorDialog(true);
            console.log(`네이티브: 파일 크기 초과로 ${oversizedFiles.length}개 파일 제외`);
          }

          const currentCount = uploadedMedia.length;
          const maxMedia = uploadPolicyRef.current?.maxMediaCount;
          if (!maxMedia) {
            setErrorMessage("업로드 정책을 불러오지 못했습니다.");
            setShowErrorDialog(true);
            return;
          }

          if (currentCount + validItems.length > maxMedia) {
            setErrorMessage(
              `사진/동영상은 최대 ${maxMedia}개까지 업로드 가능합니다. (현재: ${currentCount}개)`
            );
            setShowErrorDialog(true);
            return;
          }

          const newMedia = validItems
            .slice(0, maxMedia - currentCount)
            .map((item) => ({
              ImgID: item.id,
              ImgURL: item.thumbnail,
              mediaType: item.type,
              fileName: item.fileName,
              isNative: true,
            }));

          setUploadedMedia((prev) => [...prev, ...newMedia]);
          console.log("네이티브 미디어 선택 완료 (일괄):", validItems.length, "개");
        } else if (result.error && result.error !== "선택 취소") {
          clearLoadingTimeout();
          toggleLoading(false);
          setErrorMessage(result.error);
          setShowErrorDialog(true);
        } else if (!result.status) {
          clearLoadingTimeout();
          toggleLoading(false);
        }
      } catch (e) {
        console.error("onMediaSelectResult 파싱 에러:", e);
        clearLoadingTimeout();
        toggleLoading(false);
      }
    };

    // 업로드 진행률 콜백
    window.onMediaUploadProgress = (jsonString: string) => {
      try {
        const progress = JSON.parse(jsonString);
        console.log("업로드 진행률:", progress);

        const hasItemProgress =
          Array.isArray(progress.items) && progress.items.length > 0;
        if (nativeUploadInProgressRef.current && !hasItemProgress) {
          return;
        }

        // 진행률 상태 업데이트
        setUploadProgress(progress.overallPercent ?? progress.percent ?? 0);
        setCurrentFile(progress.current || 1);  // 현재 파일 번호 업데이트
        if (hasItemProgress) {
          setUploadProgressItems(progress.items);
        }
      } catch (e) {
        console.error("onMediaUploadProgress 파싱 에러:", e);
      }
    };

    // 업로드 완료 콜백
    window.onMediaUploadResult = (jsonString: string) => {
      // UI 잠금 해제
      if (window.webkit?.messageHandlers?.mung) {
        window.webkit.messageHandlers.mung.postMessage(["lockNavigation", false]);
      }
      if (window.mung?.lockNavigation) {
        window.mung.lockNavigation(false);
      }

      try {
        const result = JSON.parse(jsonString);
        toggleLoading(false);
        setIsUploading(false);
        nativeUploadInProgressRef.current = false;
        setUploadProgress(0);
        setCurrentFile(1);  // 리셋
        setUploadProgressItems([]);

        if (result.success) {
          console.log("네이티브 업로드 완료:", result);
          queryClient.removeQueries({
            queryKey: ["businessPhotoAlbum"],
          });
          setShowSuccessDialog(true);
        } else if (result.cancelled) {
          // 사용자가 취소한 경우
          console.log("네이티브 업로드 취소됨");
        } else {
          setErrorMessage(
            result.error || "업로드에 실패했습니다. 다시 시도해주세요."
          );
          setShowErrorDialog(true);
        }
      } catch (e) {
        console.error("onMediaUploadResult 파싱 에러:", e);
        toggleLoading(false);
        setIsUploading(false);
        nativeUploadInProgressRef.current = false;
        setUploadProgress(0);
        setCurrentFile(1);  // 리셋
        setUploadProgressItems([]);
        setErrorMessage("업로드 결과 처리 중 오류가 발생했습니다.");
        setShowErrorDialog(true);
      }
    };

    // 콜백 정리 (clearSelectedMedia는 페이지 언마운트 시에만)
    return () => {
      clearLoadingTimeout();
      window.onMediaSelectResult = undefined;
      window.onMediaUploadProgress = undefined;
      window.onMediaUploadResult = undefined;
    };
  }, [toggleLoading, queryClient]);

  // 페이지 떠날 때 네이티브 미디어 정리 (별도 useEffect)
  useEffect(() => {
    return () => {
      if (isIOSNative() && window.webkit?.messageHandlers?.mung) {
        window.webkit.messageHandlers.mung.postMessage(["clearSelectedMedia"]);
      }
    };
  }, []);

  // 미디어 삭제 (이미지/동영상)
  const handleMediaDelete = useCallback(
    (imgId: number | string) => {
      console.log("미디어 삭제 시작:", imgId);

      setUploadedMedia((prev) => {
        const targetMedia = prev.find((media) => media.ImgID === imgId);
        const filteredMedia = prev.filter((media) => media.ImgID !== imgId);

        // iOS 네이티브 미디어인 경우 네이티브에도 삭제 알림
        if (
          targetMedia?.isNative &&
          isIOSNative() &&
          window.webkit?.messageHandlers?.mung
        ) {
          window.webkit.messageHandlers.mung.postMessage([
            "removeSelectedMedia",
            imgId,
          ]);
        }

        // Android/Web: uploadedFiles도 같은 인덱스로 삭제
        if (!targetMedia?.isNative) {
          setUploadedFiles((prevFiles) => {
            const deletedIndex = prev.findIndex(
              (media) => media.ImgID === imgId
            );
            if (deletedIndex !== -1) {
              return prevFiles.filter((_, index) => index !== deletedIndex);
            }
            return prevFiles;
          });
        }

        return filteredMedia;
      });
    },
    []
  );

  // 미디어 선택 함수 (이미지/동영상)
  const handleMediaSelect = useCallback(() => {
    if (!uploadPolicy) return;

    syncNativeUploadPolicy();

    const selectionLimit = uploadPolicy.maxMediaCount - uploadedMedia.length;
    const allowedMediaType = getAllowedMediaType(uploadPolicy.allowedTypes);
    if (!allowedMediaType) {
      setErrorMessage("업로드 가능한 미디어 타입이 설정되지 않았습니다.");
      setShowErrorDialog(true);
      return;
    }

    if (selectionLimit <= 0) {
      setErrorMessage(`사진/동영상은 최대 ${uploadPolicy.maxMediaCount}개까지 업로드 가능합니다.`);
      setShowErrorDialog(true);
      return;
    }

    // iOS 네이티브 앱인 경우
    if (isIOSNative() && window.webkit?.messageHandlers?.mung) {
      toggleLoading(true);
      // 10초 타임아웃 - iOS에서 JS 실패 시 WebView 리로드하므로 백업용
      const pickerTimeoutId = setTimeout(() => {
        console.log("⚠️ iOS picker 타임아웃 (10초) - 로딩 강제 해제");
        toggleLoading(false);
      }, 10000);
      (window as unknown as { _pickerTimeoutId?: ReturnType<typeof setTimeout> })._pickerTimeoutId = pickerTimeoutId;

      window.webkit.messageHandlers.mung.postMessage([
        "selectMediaForUpload",
        selectionLimit,
        allowedMediaType,
      ]);
      return;
    }

    // Android 네이티브 앱인 경우
    if (isAndroidNative() && window.mung?.selectMediaForUpload) {
      toggleLoading(true);
      // 10초 타임아웃
      const pickerTimeoutId = setTimeout(() => {
        console.log("⚠️ Android picker 타임아웃 (10초) - 로딩 강제 해제");
        toggleLoading(false);
      }, 10000);
      (window as unknown as { _pickerTimeoutId?: ReturnType<typeof setTimeout> })._pickerTimeoutId = pickerTimeoutId;

      window.mung.selectMediaForUpload(selectionLimit, allowedMediaType);
      return;
    }

    // Web: 기존 방식
    const input = document.createElement("input");
    input.type = "file";

    input.accept = getFileInputAccept(uploadPolicy.allowedTypes);
    input.multiple = true;

    input.onchange = (event) => {
      const files = (event.target as HTMLInputElement).files;
      if (files) {
        toggleLoading(true);

        const { validFiles, errors, mediaTypes } = validateMediaFiles(
          files,
          uploadPolicy.maxFileSizeMb
        );

        if (errors.length > 0) {
          setShowValidationErrorDialog(true);
          toggleLoading(false);
        }

        if (validFiles.length > 0) {
          const currentMediaCount = uploadedMedia.length;
          const maxMedia = uploadPolicy.maxMediaCount;

          if (currentMediaCount + validFiles.length > maxMedia) {
            setErrorMessage(
              `사진/동영상은 최대 ${maxMedia}개까지 업로드 가능합니다. (현재: ${currentMediaCount}개)`
            );
            setShowErrorDialog(true);
            toggleLoading(false);
            return;
          }

          const processFiles = async () => {
            const newMedia: Array<{
              ImgID: number;
              ImgURL: string;
              mediaType: "image" | "video";
            }> = [];
            const newFiles: File[] = [];

            for (let i = 0; i < validFiles.length; i++) {
              const file = validFiles[i];
              const mediaType =
                mediaTypes.find((mt) => mt.file === file)?.type || "image";

              const mediaUrl = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                  resolve(e.target?.result as string);
                };
                reader.readAsDataURL(file);
              });

              const newMediaItem = {
                ImgID: Date.now() + i + Math.random(),
                ImgURL: mediaUrl,
                mediaType: mediaType as "image" | "video",
                fileName: file.name,
              };

              newMedia.push(newMediaItem);
              newFiles.push(file);
            }

            setUploadedMedia((prev) => [...prev, ...newMedia]);
            setUploadedFiles((prev) => [...prev, ...newFiles]);
            toggleLoading(false);
          };

          processFiles();
        } else {
          toggleLoading(false);
        }
      } else {
        toggleLoading(false);
      }

      input.remove();
    };

    input.click();
  }, [syncNativeUploadPolicy, toggleLoading, uploadPolicy, uploadedMedia.length]);

  // 네이티브 UI 잠금 요청 함수
  const lockNavigation = useCallback((lock: boolean) => {
    // iOS
    if (window.webkit?.messageHandlers?.mung) {
      window.webkit.messageHandlers.mung.postMessage(["lockNavigation", lock]);
    }
    // Android
    if (window.mung?.lockNavigation) {
      window.mung.lockNavigation(lock);
    }
  }, []);

  // 브라우저 뒤로가기/새로고침 방지
  useEffect(() => {
    if (!isUploading) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "업로드가 진행 중입니다. 페이지를 떠나시겠습니까?";
      return e.returnValue;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isUploading]);

  // 업로드 취소 핸들러
  const handleCancelUpload = useCallback(() => {
    // UI 잠금 해제
    lockNavigation(false);

    // iOS 네이티브 취소
    if (isIOSNative() && window.webkit?.messageHandlers?.mung) {
      window.webkit.messageHandlers.mung.postMessage(["cancelUpload"]);
    }

    // Android 네이티브 취소
    if (isAndroidNative() && window.mung?.cancelUpload) {
      window.mung.cancelUpload();
    }

    // Web 취소
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setIsUploading(false);
    nativeUploadInProgressRef.current = false;
    setUploadProgress(0);
    setCurrentFile(1);  // 리셋
    setUploadProgressItems([]);
    toggleLoading(false);
    navigate({ to: "/business/layout/gallery" });
  }, [navigate, toggleLoading, lockNavigation]);

  // 저장 버튼 핸들러
  const handleSave = useCallback(async () => {
    if (!selectedPet || uploadedMedia.length === 0 || !uploadPolicy) return;

    console.log("선택된 반려견:", selectedPet);
    syncNativeUploadPolicy();

    // 업로드 시작
    setIsUploading(true);
    setUploadProgress(0);
    setCurrentFile(1);  // 초기화
    setUploadProgressItems([]);
    lockNavigation(true);  // UI 잠금

    // iOS 네이티브 업로드 (네이티브 미디어가 있는 경우)
    const hasNativeMedia = uploadedMedia.some((m) => m.isNative);
    const nativeProgressItems = uploadedMedia
      .filter((media) => media.isNative)
      .map((media, index) => ({
        id: String(media.ImgID),
        fileName: media.fileName || `미디어 ${index + 1}`,
        type: media.mediaType,
        percent: 0,
        status: "waiting" as const,
      }));
    const uploadMuteYn = muteVideos ? "Y" : "N";
    const nativeUploadOptions = JSON.stringify({ muteYn: uploadMuteYn });
    if (
      isIOSNative() &&
      hasNativeMedia &&
      window.webkit?.messageHandlers?.mung
    ) {
      nativeUploadInProgressRef.current = true;
      setUploadProgressItems(nativeProgressItems);
      window.webkit.messageHandlers.mung.postMessage([
        "uploadSelectedMedia",
        parseInt(selectedPet),
        nativeUploadOptions,
      ]);
      return;
    }

    // Android 네이티브 업로드 (네이티브 미디어가 있는 경우)
    if (
      isAndroidNative() &&
      hasNativeMedia &&
      window.mung?.uploadSelectedMedia
    ) {
      nativeUploadInProgressRef.current = true;
      setUploadProgressItems(nativeProgressItems);
      window.mung.uploadSelectedMedia(parseInt(selectedPet), nativeUploadOptions);
      return;
    }

    // Web: 진행률 지원 업로드
    if (uploadedFiles.length > 0) {
      // AbortController 생성
      abortControllerRef.current = new AbortController();

      try {
        const response = await uploadBusinessPhoto({
          petSeq: parseInt(selectedPet),
          files: uploadedFiles,
          muteYn: uploadMuteYn,
          onProgress: (progress) => {
            setUploadProgress(progress);
          },
          signal: abortControllerRef.current.signal,
        });

        console.log("사진 업로드 성공:", response);
        queryClient.removeQueries({
          queryKey: ["businessPhotoAlbum"],
        });
        setIsUploading(false);
        nativeUploadInProgressRef.current = false;
        setUploadProgress(0);
        setCurrentFile(1);  // 리셋
        setUploadProgressItems([]);
        setShowSuccessDialog(true);
      } catch (error: any) {
        setIsUploading(false);
        nativeUploadInProgressRef.current = false;
        setUploadProgress(0);
        setCurrentFile(1);  // 리셋
        setUploadProgressItems([]);

        // 사용자 취소인 경우
        if (error.name === "AbortError" || error.name === "CanceledError") {
          console.log("업로드 취소됨");
          return;
        }

        console.error("사진 업로드 실패:", error);
        setErrorMessage(
          "사진/동영상 업로드에 실패했습니다. 다시 시도해주세요."
        );
        setShowErrorDialog(true);
      }
    } else {
      setIsUploading(false);
      setErrorMessage("업로드할 파일이 없습니다.");
      setShowErrorDialog(true);
    }
  }, [
    selectedPet,
    uploadedMedia,
    uploadedFiles,
    queryClient,
    muteVideos,
    syncNativeUploadPolicy,
    uploadPolicy,
  ]);

  // API 데이터를 사용한 반려견 목록
  const petList =
    photoFormData?.data?.map((pet) => ({
      value: pet.petSeq.toString(),
      label: pet.petName,
    })) || [];

  // 로딩 상태 처리
  if (isLoading || isPolicyLoading) {
    return (
      <>
        <LayoutHeader
          title={"사진/동영상 등록"}
          hasBackBtn
          hasFootPrint={false}
        />
        <div className="flex flex-col flex-auto bg-[#F5F5F5] items-center justify-center">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      </>
    );
  }

  // 에러 상태 처리
  if (error || policyError || !uploadPolicy) {
    return (
      <>
        <LayoutHeader
          title={"사진/동영상 등록"}
          hasBackBtn
          hasFootPrint={false}
        />
        <div className="flex flex-col flex-auto bg-[#F5F5F5] items-center justify-center">
          <div className="text-red-500">
            데이터를 불러오는 중 오류가 발생했습니다.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <LayoutHeader title={"사진 등록"} hasBackBtn hasFootPrint={false} />
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5]">
        <div className="flex flex-col flex-auto gap-4 px-5 pb-12 rounded-t-3xl">
          <div className="my-3">
            <ValidateSelectList
              label="반려동물 선택"
              placeholder="반려동물 이름 선택"
              options={petList}
              onChange={(selectedOption) =>
                setSelectedPet(selectedOption?.value || "")
              }
              value={
                selectedPet
                  ? petList.find((pet) => pet.value === selectedPet)
                  : null
              }
            />
          </div>
          {/* 갤러리 목록 */}
          <div className="flex overflow-y-auto flex-col gap-2">
            {/* 미디어 개수 및 제한 안내 표시 */}
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  사진 {imageCount}개 · 동영상 {videoCount}개
                </span>
                <button
                  type="button"
                  className="flex cursor-pointer items-center gap-2"
                  aria-pressed={muteVideos}
                  onClick={() => setMuteVideos((prev) => !prev)}
                >
                  <span className="text-sm text-gray-700">
                    동영상 기본 음소거
                  </span>
                  <span
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full border transition-colors ${
                      muteVideos
                        ? "border-orange-400 bg-orange-400"
                        : "border-gray-300 bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 rounded-full bg-white transition-transform ${
                        muteVideos ? "translate-x-5" : "translate-x-1"
                      }`}
                    />
                  </span>
                </button>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-1 sm:grid-cols-4">
              {/* 업로드된 미디어들 (이미지/동영상) */}
              {uploadedMedia.map((data) => (
                <div
                  key={data.ImgID}
                  className="overflow-hidden relative bg-white rounded-lg cursor-pointer aspect-square"
                  onClick={bindClick(() => handleMediaDelete(data.ImgID))}
                >
                  {data.mediaType === "video" ? (
                    <>
                      {data.isNative ? (
                        // iOS 네이티브: 썸네일이 있으면 사용, 없으면 플레이스홀더
                        data.ImgURL ? (
                          <img
                            src={data.ImgURL}
                            alt={`Video thumbnail ${data.ImgID}`}
                            className="object-cover w-full h-full rounded-lg"
                          />
                        ) : (
                          // 플레이스홀더: 회색 배경 (썸네일 생성 비활성화로 인해)
                          <div className="flex justify-center items-center w-full h-full bg-gray-300 rounded-lg">
                            <svg
                              className="w-8 h-8 text-gray-500"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                            </svg>
                          </div>
                        )
                      ) : (
                        // [성능 개선] Android/Web: 플레이스홀더로 대체 (VideoThumbnail Canvas 썸네일 생성 비활성화)
                        <div className="flex justify-center items-center w-full h-full bg-gray-300 rounded-lg">
                          <svg
                            className="w-8 h-8 text-gray-500"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                          </svg>
                        </div>
                      )}
                      {/* 동영상 재생 아이콘 오버레이 */}
                      <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-20 rounded-lg pointer-events-none">
                        <svg
                          className="w-12 h-12 text-white opacity-80"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </>
                  ) : (
                    <img
                      src={data.ImgURL}
                      alt={`Uploaded media ${data.ImgID}`}
                      className="object-cover w-full h-full rounded-lg"
                    />
                  )}

                  {/* 삭제 오버레이 (useConfirmTouch 사용) */}
                  <AnimatePresence>
                    {idle && (
                      <motion.div
                        className="flex absolute inset-0 bg-black bg-opacity-30 pointer-events-none"
                        initial={{
                          opacity: 0,
                        }}
                        animate={{
                          opacity: 1,
                        }}
                        exit={{
                          opacity: 0,
                        }}
                      >
                        <img src={trashSrc} alt="삭제버튼" className="m-auto" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}

              {/* 추가 버튼 */}
              {canAddMedia && (
                <button
                  type="button"
                  onClick={handleMediaSelect}
                  className="relative cursor-pointer aspect-square border-[1px] rounded-lg border-gray-200 bg-white flex items-center justify-center hover:bg-gray-50 transition-colors"
                >
                  <img src={plusCircle} alt="plusCircle" />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="sticky bottom-14 flex flex-col gap-2 px-5 py-3 bg-[#F5F5F5]">
          <div className="text-center text-xs text-gray-400">
            <p>사진/동영상은 합산 최대 {uploadPolicy.maxMediaCount}개까지 업로드 가능합니다.</p>
            <p>(동영상 업로드는 와이파이 환경에서 업로드 해주세요.)</p>
          </div>
          <Button
            className="w-full bg-[#F5F5F5]"
            style={{ pointerEvents: "auto" }}
            onClick={handleSave}
            disabled={!selectedPet || uploadedMedia.length === 0}
          >
            <div className="py-3 mx-auto w-full text-base font-semibold text-center text-white bg-orange-400 rounded-full">
              저장하기
            </div>
          </Button>
        </div>
      </div>

      {/* 성공 Dialog */}
      <Dialog
        visible={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
          navigate({ to: "/business/layout/gallery" });
        }}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">업로드 완료</h3>
          <p className="mb-4 text-gray-600">
            사진/동영상이 성공적으로 업로드되었습니다.
          </p>
          <button
            onClick={() => {
              setShowSuccessDialog(false);
              navigate({ to: "/business/layout/gallery" });
            }}
            className="px-4 py-2 text-white bg-orange-500 rounded-lg transition-colors hover:bg-orange-600"
          >
            확인
          </button>
        </div>
      </Dialog>

      {/* 에러 Dialog */}
      <Dialog
        visible={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">업로드 실패</h3>
          <p className="mb-4 text-gray-600">{errorMessage}</p>
          <button
            onClick={() => setShowErrorDialog(false)}
            className="px-4 py-2 text-white bg-orange-500 rounded-lg transition-colors hover:bg-orange-600"
          >
            확인
          </button>
        </div>
      </Dialog>

      {/* 파일 검증 에러 Dialog */}
      <Dialog
        visible={showValidationErrorDialog}
        onClose={() => setShowValidationErrorDialog(false)}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">파일 형식 오류</h3>
          <p className="mb-4 text-gray-600">
            다음 파일들이 업로드되지 않았습니다:
          </p>
          <p className="mb-4 text-sm text-gray-500">
            이미지 또는 동영상 파일만 업로드 가능합니다.
            <br />
            이미지: JPG, PNG, GIF, WEBP, BMP, SVG
            <br />
            동영상: MP4, MOV, AVI, WEBM, MKV, M4V
          </p>
          <button
            onClick={() => setShowValidationErrorDialog(false)}
            className="px-4 py-2 text-white bg-orange-500 rounded-lg transition-colors hover:bg-orange-600"
          >
            확인
          </button>
        </div>
      </Dialog>

      {/* 업로드 진행률 모달 */}
      <UploadProgressModal
        visible={isUploading}
        progress={uploadProgress}
        onCancel={handleCancelUpload}
        currentFile={currentFile}
        totalFiles={uploadedFiles.length || uploadedMedia.filter(m => m.isNative).length}
        items={uploadProgressItems}
      />
    </>
  );
}
